/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
// @ts-ignore: side-effect CSS import without type declarations
import 'devextreme/dist/css/dx.light.css';
import DataGrid, {
  Column,
  // Paging,
  // Pager,
  FilterRow,
  HeaderFilter,
  FilterPanel,
  Sorting,
  Grouping,
  GroupPanel,
  RemoteOperations,
  Scrolling,
  Summary,
  GroupItem,
  StateStoring,
  ColumnFixing,
  DataGridTypes,
} from 'devextreme-react/data-grid';
import CustomStore from 'devextreme/data/custom_store';
import { locale, loadMessages } from 'devextreme/localization';
import esMessages from 'devextreme/localization/messages/es.json';
import { allColumns } from '../../dexextremeCollumns';
import { Box } from '@mui/material';
import { FilterOption, FiltersSection } from '../../../components/filters-section';
import { getCompanies, getConfiguration, getPeriods, getSchemes } from '../../../store/slices/account-entries';
import { RootState } from '../../../store';
import { useSelector } from 'react-redux';
import { VFlexColumnConfig } from '../../../interfaces/accountEntries.interface';
import { convertTwipsToPixels, mapDataTypeToDevExpressDataType } from '../../../utils/tools';
import DateSelectorButton from '../../../components/DateSelectorButton';
import { AvailableCombination, dataGridData, fetchAvailableCombinations } from '../../../api/accountApi';
import GridConfigModal from './GridConfigModal';
import { getUserPreferences, saveUserPreferences } from '../../../api/apiPreferences';
import { InvoiceModal } from './InvoiceModal';

loadMessages(esMessages);
locale('es');

interface GridState {
  columns: { dataField: string; width: number; visible: boolean }[];
  sorting: any[];
  filterValue: any;
  selectedRowKeys: any[];
  summaryColumns: string[];
  groupableColumns: string[];
  fixedColumns: string[];
}

type LoadOptions = any;

const defaultState: GridState = {
  columns: allColumns.map(col => ({
    dataField: col.dataField,
    width: col.width || 100,
    visible: true,
  })),
  sorting: [],
  filterValue: null,
  selectedRowKeys: [],
  summaryColumns: ['Debito', 'Credito'],
  groupableColumns: allColumns.filter(col => col.dataField !== 'OID').map(col => col.dataField),
  fixedColumns: [],
};

const loadGridState = async (): Promise<GridState> => {
  const stored = localStorage.getItem('gridState');
  if (stored) {
    const parsed = JSON.parse(stored);
    return { ...defaultState, ...parsed };
  }
  return defaultState;
};

const saveGridState = async (state: GridState): Promise<void> => {
  localStorage.setItem('gridState', JSON.stringify(state));
};

const buildColumnsFromConfig = (config: VFlexColumnConfig[]): any[] => {
  return config.map(item => ({
    dataField: item.Campo,
    caption: item.Caption,
    dataType: mapDataTypeToDevExpressDataType(item.TipoDato),
    width: convertTwipsToPixels(item.Ancho),
    visible: item.Visible,
    allowHiding: true,
    allowGrouping: item.Campo !== 'OID',
    allowSorting: true,
    allowFiltering: item.Filtro,
    summary: item.Sumar,
  }));
};

const getNumberFormat = () => {
  return (value: number | null | undefined): string => {
    if (value == null) return '';
    return value.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      useGrouping: true,
    });
  };
};

const buildFixedFilter = (filterValues: Record<string, string>): any[] | null => {
  const conditions: any[] = [];
  conditions.push([
    'CodPeriodoContable', 
    filterValues.accumulated === '1' ? '<=' : '=', 
    filterValues.period
  ]);

  if (filterValues.accountingEntry === '0') {
    conditions.push('and');
    conditions.push(['Refleja', '=', 0]);
  }


  if (['0', '1'].includes(filterValues.entry)) {
    conditions.push('and');
    conditions.push(['Descuadrado', '=', filterValues.entry]);
  }

  if (conditions.length === 1) return conditions[0];
  return conditions;
};

const INITIAL_FILTER_VALUES = {
  folder: 'all',
  company: '',
  scheme: '',
  period: '',
  accumulated: '0',
  accountingEntry: '1',
  entry: '-1',
}

export const AccountingEntriesGrid: React.FC = () => {
  const { companyselected, connectionId } = useSelector((state: RootState) => state.auth);
  const { vFlexColumnsConfig, companies, scheme, periods } = useSelector((state: RootState) => state.accountEntries);

  const [filterValues, setFilterValues] = useState<Record<string, string>>(INITIAL_FILTER_VALUES);

  const [dataSourceInstance, setDataSourceInstance] = useState<CustomStore | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [columnsOrder, setColumnsOrder] = useState<string[]>([]);
  const [groupable, setGroupable] = useState<Record<string, boolean>>({});
  const [summary, setSummary] = useState<Record<string, boolean>>({});
  const [visible, setVisible] = useState<Record<string, boolean>>({});
  const [fixed, setFixed] = useState<Record<string, boolean>>({});

  const [availableCombinations, setAvailableCombinations] = useState<AvailableCombination[]>([]);
  const [validCombinationMap, setValidCombinationMap] = useState<Set<string>>(new Set());

  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [selectedRowForModal, setSelectedRowForModal] = useState<any>(null);

  const [grouping, setGrouping] = useState<string[]>(() => {
    const stored = localStorage.getItem('gridGrouping');
    return stored ? JSON.parse(stored) : [];
  });

  useEffect(() => {
    localStorage.setItem('gridGrouping', JSON.stringify(grouping));
  }, [grouping]);

  useEffect(() => {
    fetchAvailableCombinations().then(data => {
      setAvailableCombinations(data);
      const map = new Set<string>();
      data.forEach(c => map.add(`${c.CompanyCode}|${c.SchemeCode}|${c.Year}`));
      setValidCombinationMap(map);
    });
  }, []);

  const activeColumns = useMemo(() => {
    if (vFlexColumnsConfig === null) return [];
    if (vFlexColumnsConfig.length === 0) return allColumns.map(col => ({ ...col, summary: false }));
    return buildColumnsFromConfig(vFlexColumnsConfig);
  }, [vFlexColumnsConfig]);

  const useLocalStorage = vFlexColumnsConfig?.length === 0;

  const orderedColumns = useMemo(() => {
    if (!columnsOrder.length) return activeColumns;
    const ordered = columnsOrder
      .map(field => activeColumns.find(col => col.dataField === field))
      .filter((col): col is any => col !== undefined);
    const missing = activeColumns.filter(col => !columnsOrder.includes(col.dataField));
    return [...ordered, ...missing];
  }, [activeColumns, columnsOrder]);

  const isCombinationValid = useMemo(() => {
    if (!filterValues.company || !filterValues.scheme || !filterValues.period) return false;
    const year = filterValues.period.substring(0, 4);
    return validCombinationMap.has(`${filterValues.company}|${filterValues.scheme}|${year}`);
  }, [filterValues, validCombinationMap]);

  const isSearchDisabled = !isCombinationValid;

  useEffect(() => {
    if (!companyselected?.Token || !connectionId || !filterValues.company) return;
    getSchemes(filterValues.company);
    getPeriods(filterValues.company);
  }, [companyselected?.Token, connectionId, filterValues.company]);

  useEffect(() => {
    if (!companyselected?.Token || !connectionId) return;
    getConfiguration();
    getUserPreferences().then(preferences => {
      let accountingViewConfig = preferences?.data?.accountingViewConfig;
      if (!accountingViewConfig) {
        accountingViewConfig = JSON.parse(localStorage.getItem('userPreferences') || '{}')?.accountingViewConfig || {};
      } 
      setFilterValues({ ...filterValues, ...accountingViewConfig});
    }).finally(() => {
      getCompanies();
    });
  }, [companyselected?.Token, connectionId]);

  const handleSearch = useCallback(() => {
  console.log('filterValues, isSearchDisabled', filterValues, isSearchDisabled)
    if (isSearchDisabled) return;
    const periodCode = filterValues.period;
    const year = periodCode.substring(0, 4);
    const baseFilters = {
      codEmpresa: filterValues.company,
      codEsquema: filterValues.scheme,
      codAno: year,
    };
    const fixedFilters = buildFixedFilter(filterValues);
    
    const newStore = new CustomStore({
      key: 'OID',
      load: async (loadOptions: LoadOptions) => {
        let finalFilter = loadOptions.filter;
        if (loadOptions?.filter?.length) {
          finalFilter = [fixedFilters, 'and', loadOptions.filter];
        } else {
          finalFilter = fixedFilters;
        }

        console.log('loadOptions.totalSummary', loadOptions.totalSummary)
        console.log('loadOptions.groupSummary', loadOptions.groupSummary)
        
        const params: Record<string, any> = {
          ...loadOptions,
          codEmpresa: baseFilters.codEmpresa,
          codEsquema: baseFilters.codEsquema,
          codAno: baseFilters.codAno,
          userData: JSON.stringify(loadOptions.userData),
          sort: JSON.stringify(loadOptions.sort),
          filter: JSON.stringify(finalFilter),
          group: JSON.stringify(loadOptions.group),
          totalSummary: JSON.stringify(loadOptions.totalSummary),
          groupSummary: JSON.stringify(loadOptions.groupSummary),
        };
        return dataGridData(params);
      }
    });
    setDataSourceInstance(newStore);
  }, [filterValues, isSearchDisabled]);


  const loadConfig = async () => {
    if (!useLocalStorage && activeColumns.length > 0) {
      const order = activeColumns.map(c => c.dataField);
      setColumnsOrder(order);

      const groupableMap: Record<string, boolean> = {};
      const summaryMap: Record<string, boolean> = {};
      const visibleMap: Record<string, boolean> = {};
      const fixedMap: Record<string, boolean> = {};
      console.log('activeColumns', activeColumns)
      activeColumns.forEach(col => {
        groupableMap[col.dataField] = col.dataField !== 'OID';
        if (col.summary === true) summaryMap[col.dataField] = col.summary === true;
        if (col.visible !== false) visibleMap[col.dataField] = col.visible !== false;
        fixedMap[col.dataField] = false;
      });
      console.log('summaryMap', summaryMap)

      setGroupable(groupableMap);
      setSummary(summaryMap);
      setVisible(visibleMap);
      setFixed(fixedMap);
      return;
    }

    if (useLocalStorage) {
      const state = await loadGridState();
      const order = state.columns.map(c => c.dataField);
      setColumnsOrder(order);

      const groupableMap: Record<string, boolean> = {};
      const summaryMap: Record<string, boolean> = {};
      const visibleMap: Record<string, boolean> = {};
      const fixedMap: Record<string, boolean> = {};

      allColumns.forEach(col => {
        groupableMap[col.dataField] = state.groupableColumns?.includes(col.dataField) ?? (col.dataField !== 'OID');
        if (col.dataType === 'number') {
          summaryMap[col.dataField] = state.summaryColumns?.includes(col.dataField) ?? false;
        }
        const columnState = state.columns.find(c => c.dataField === col.dataField);
        visibleMap[col.dataField] = columnState?.visible ?? true;
        fixedMap[col.dataField] = state.fixedColumns?.includes(col.dataField) ?? false;
      });

      setGroupable(groupableMap);
      setSummary(summaryMap);
      setVisible(visibleMap);
      setFixed(fixedMap);
    }
  };

  useEffect(() => {
    if (activeColumns.length > 0 || useLocalStorage) loadConfig();
  }, [activeColumns]);

  const onContextMenuPreparing = useCallback((e: any) => {
    if (e.row) {
        const rowData = e.row.data;

        if (!rowData?.TipoDocumento) {
          return
        }

        const menuItems = [];

        menuItems.push({
            text: "Ver documento",
            onItemClick: () => {
                setSelectedRowForModal(rowData);
                setInvoiceModalOpen(true);
            }
        });
        if (rowData?.TipoDocumento === "2") {
        } else {

            menuItems.push({
                text: `Documento tipo: ${rowData?.TipoDocumento ?? "N/A"}`,
                disabled: true
            });
        }

        e.items = menuItems;
    }
}, []);

  const handleSaveConfig = (newOrder: string[], newVisible: Record<string, boolean>, newGroupable: Record<string, boolean>, newSummary: Record<string, boolean>, newFixed: Record<string, boolean>) => {
    const allFields = activeColumns.map(col => col.dataField);
    const fullOrder = [...newOrder, ...allFields.filter(f => !newOrder.includes(f))];
    setColumnsOrder(fullOrder);
    setVisible(newVisible);
    setGroupable(newGroupable);
    setSummary(newSummary);
    setFixed(newFixed);
    setModalOpen(false);
  };

  const handleFilterChange = (filterId: string, value: string) => {
    let newFilterValues = { [filterId]: value };
    if (filterId === 'company') {
      newFilterValues.scheme = '';
      newFilterValues.period = '';
    }
    setFilterValues(prev => ({ ...prev, ...newFilterValues }));
    saveUserPreferences({accountingViewConfig: { ...filterValues, ...newFilterValues } });
    console.log('Saving user preferences with accountingViewConfig:', { ...filterValues, ...newFilterValues });
    localStorage.setItem('userPreferences', JSON.stringify({ accountingViewConfig: { ...filterValues, ...newFilterValues } }));
  };

  const handleGroupingChanged = (e: any) => {
    if (e.name === 'grouping') {
      const newGrouping = e.value.map((g: any) => g.selector);
      setGrouping(newGrouping);
    }
  };

  const enrichedFilters = useMemo((): FilterOption[] => {
    const empresaOpts = companies.map(company => {
      const hasAnyCombination = availableCombinations.some(c => c.CompanyCode === company.CodEmpresa);
      return {
        label: company.Descripcion,
        value: company.CodEmpresa,
        disabled: !hasAnyCombination,
      };
    });

    let esquemaOpts: { label: string; value: string; disabled?: boolean }[] = [];
    if (filterValues.company) {
      esquemaOpts = scheme.map(s => {
        const hasCombination = availableCombinations.some(
          c => c.CompanyCode === filterValues.company && c.SchemeCode === s.CodEsquemaContable
        );
        return {
          label: `${s.CodEsquemaContable} - ${s.Descripcion}`,
          value: s.CodEsquemaContable,
          disabled: !hasCombination,
        };
      });
    } else {
      esquemaOpts = scheme.map(s => ({
        label: `${s.CodEsquemaContable} - ${s.Descripcion}`,
        value: s.CodEsquemaContable,
        disabled: true,
      }));
    }

    const allYears = Array.from(new Set(periods.map(p => p.CodPeriodoContable.substring(0, 4))));
    let anioOpts: { label: string; value: string; disabled?: boolean }[] = [];
    if (filterValues.company && filterValues.scheme) {
      anioOpts = allYears.map(ano => {
        const hasCombination = availableCombinations.some(
          c => c.CompanyCode === filterValues.company && c.SchemeCode === filterValues.scheme && c.Year === ano
        );
        return { label: ano, value: ano, disabled: !hasCombination };
      });
    } else {
      anioOpts = allYears.map(ano => ({ label: ano, value: ano, disabled: true }));
    }

    const periodDates = periods.map(p => {
      const ano = p.CodPeriodoContable.substring(0, 4);
      const isDisabled = anioOpts.find(opt => opt.value === ano)?.disabled ?? true;
      return {
        DesPeriodo: `${p.CodPeriodoContable} - ${p.Descripcion}`,
        CodPeriodo: p.CodPeriodoContable,
        Anio: ano,
        disabled: isDisabled,
      };
    });

    return [
      {
        width: 150,
        type: 'search',
        label: 'Carpeta',
        value: 'folder',
        options: [{ label: 'Todas', value: 'all' }],
      },
      {
        width: 250,
        type: 'search',
        label: 'Empresa',
        value: 'company',
        options: empresaOpts,
      },
      {
        width: 250,
        type: 'search',
        label: 'Esquema',
        value: 'scheme',
        options: esquemaOpts,
      },
      {
        label: 'Periodo',
        value: 'period',
        type: 'custom' as const,
        component: (
          <DateSelectorButton
            dates={periodDates}
            selectedDate={filterValues.period}
            onDateChange={(codigo, descripcion) => {
              handleFilterChange('period', codigo);
            }}
            groupBy={'Anio'}
            buttonColor="secondary"
            modalTitle="Seleccionar período"
            placeholder="Elige una fecha"
            accumulated={filterValues.accumulated === '1'}
            onAccumulatedChange={(value) => {
              handleFilterChange('accumulated', value ? '1' : '0');
            }}
          />
        ),
      },
      {
        width: 150,
        type: 'select' as const,
        label: 'Asiento Contable',
        value: 'accountingEntry',
        options: [
          { label: 'Detallado', value: '1' },
          { label: 'Sin Reflejas', value: '0' },
        ],
      },
      {
        width: 150,
        type: 'select' as const,
        label: 'Asiento',
        value: 'entry',
        options: [
          { label: 'Todos', value: '-1' },
          { label: 'Descuadrados', value: '0' },
          { label: 'Cuadrados', value: '1' },
        ],
      },
    ];
  }, [companies, scheme, periods, availableCombinations, filterValues.company, filterValues.scheme, filterValues.period, filterValues.accumulated]);

  const dataGridMemo = useMemo(() => (
    <DataGrid
      dataSource={dataSourceInstance}
      width="100%"
      height="100%"
      showBorders
      remoteOperations
      columnHidingEnabled
      allowColumnResizing
      selection={{ mode: 'single' }}
      onSelectionChanged={(e: DataGridTypes.SelectionChangedEvent) => console.log('Selected rows:', e.selectedRowKeys)}
      columnResizingMode="widget"
      allowColumnReordering
      onToolbarPreparing={e => {
        e?.toolbarOptions?.items?.unshift({
          widget: 'dxButton',
          options: { icon: 'gear', text: 'Configurar', onClick: () => setModalOpen(true), stylingMode: 'text' },
          location: 'after',
        });
      }}
      onOptionChanged={handleGroupingChanged}
      onContextMenuPreparing={onContextMenuPreparing}
      {...({ group: grouping } as any)}
    >
      <StateStoring enabled type="custom" customLoad={loadGridState} customSave={saveGridState} />
      <RemoteOperations sorting filtering grouping paging summary />
      <Scrolling mode="virtual" rowRenderingMode="virtual" />
      <Summary>
        {Object.entries(summary).map(([dataField, isActive]) => isActive && (
          <GroupItem
              key={`group-${dataField}`}
              column={dataField}
              summaryType="sum"
              alignByColumn
              customizeText={(itemInfo: { value: string | number | Date; valueText: string }) => {
                let numericValue: number | null = null;
                if (typeof itemInfo.value === 'number') {
                  numericValue = itemInfo.value;
                } else if (typeof itemInfo.value === 'string') {
                  const parsed = parseFloat(itemInfo.value);
                  if (!isNaN(parsed)) numericValue = parsed;
                }
                if (numericValue === null || isNaN(numericValue)) return '';
                return getNumberFormat()(numericValue);
              }}
          />
        ))}
      </Summary>
      <Grouping 
        autoExpandAll={false} 
        expandMode="rowClick"
        
      />
      <GroupPanel visible emptyPanelText="Arrastre una columna aquí para agrupar" />
      <FilterRow visible />
      <HeaderFilter visible />
      <FilterPanel visible />
      <Sorting mode="multiple" />
      <ColumnFixing enabled texts={{ fix: 'Fijar columna', leftPosition: 'Fijar a la izquierda', rightPosition: 'Fijar a la derecha', unfix: 'Desfijar columna' }} />

      {orderedColumns.map((col: any) => {
        const isGroupable = groupable[col.dataField] ?? (col.allowGrouping !== false);
        const isVisible = visible[col.dataField] ?? (col.visible !== false);
        const isFixed = fixed[col.dataField] ?? false;
        let columnFormat = col.format;
        if (col.dataField !== 'OID' && col.dataType === 'number') {
          columnFormat = col.format === 'currency' ? getNumberFormat() : col.format || getNumberFormat();
        }
        return (
          <Column
            key={col.dataField}
            dataField={col.dataField}
            caption={col.caption}
            dataType={col.dataType}
            width={col.width}
            format={columnFormat}
            visible={isVisible}
            allowHiding={col.allowHiding !== false}
            allowGrouping={isGroupable}
            allowSorting={col.allowSorting !== false}
            allowFiltering={col.allowFiltering !== false}
            fixed={isFixed}
            fixedPosition="left"
          />
        );
      })}

    </DataGrid>
  ), [dataSourceInstance, grouping, summary, orderedColumns, groupable, visible, fixed]);
  
  if (vFlexColumnsConfig === null) {
    return <Box sx={{ p: 4, textAlign: 'center' }}>Cargando configuración de columnas...</Box>;
  }



  console.log(
    'summary', summary,
    'useLocalStorage', useLocalStorage
  )

  return (
  <div style={{ height: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
    <Box sx={{ flexShrink: 0 }}>
      <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', backgroundColor: 'rgba(0, 0, 0, 0.02)', borderRadius: 1 }}>
        <FiltersSection
          filters={enrichedFilters as any}
          onFilterChange={handleFilterChange}
          values={filterValues as any}
          onSearch={handleSearch}
          searchButtonDisabled={isSearchDisabled}
        />
      </Box>
    </Box>
    
    <style>{`
      .dx-datagrid-group-row td {
        overflow: visible !important;
      }
      .dx-datagrid-summary-item {
        position: relative !important;
        z-index: 100 !important;
        background-color: inherit !important;
      }
      .dx-datagrid-rowsview .dx-row.dx-group-row {
        overflow: visible !important;
      }
      .dx-datagrid-group-row-container {
        position: relative !important;
        z-index: 150 !important;
        background-color: "#F7F7F7" !important;
        padding-right: 120px;
      }
    `}</style>

    <Box sx={{ flex: 1, minHeight: 0 }}>
      {dataSourceInstance ? (
        dataGridMemo
      ) : (
        <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
          Seleccione una empresa, esquema y período, luego presione Buscar.
        </Box>
      )}
    </Box>
    <InvoiceModal
      open={invoiceModalOpen}
      onClose={() => setInvoiceModalOpen(false)}
      data={selectedRowForModal || {} as any}
    />

    <GridConfigModal
      open={modalOpen}
      onClose={() => setModalOpen(false)}
      onSave={handleSaveConfig}
      initialOrder={columnsOrder}
      initialVisible={visible}
      initialGroupable={groupable}
      initialSummary={summary}
      initialFixed={fixed}
      useLocalStorage={useLocalStorage}
      allColumns={allColumns}
      activeColumns={activeColumns}
    />
  </div>
  );
};